import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
  ViewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';

import { useFirmwareStore } from '../../../+state/firmware-package/firmware-api.store';
import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';

import { FileUploadService } from './file-upload.service';

@Component({
  standalone: true,
  selector: 'remote-update-domain-file-upload',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CommonModule,
    MatButtonModule,
    FormsModule,
    MatListModule,
    MatIconModule
  ],
  templateUrl: './file-upload.html',
  styleUrl: './file-upload.css'
})
export class FileUpload implements OnInit {
  private fileService = inject(FileUploadService);
  private store = inject(StepperFormStore);
  private firmwareStore = inject(useFirmwareStore);

  @Input() currentSection = '';

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef<HTMLInputElement>;

  isDragOver = signal(false);
  processing = computed(() => this.store.processing());
  isLoading = computed(() => this.firmwareStore.packageVersion.loading());
  isEditingFirmwarePackage = computed(() => this.store.isEditingFirmwarePackage());

  files: Signal<File[]> = this.store.selectedFiles;

  get error(): string | undefined {
    return this.store.errors().fileUpload;
  }

  ngOnInit() {
    if (this.isEditingFirmwarePackage()) {
      const { fileName, fileSize } = this.store.fileUpload();
      if (fileName && fileSize > 0) {
        const loadedFile = new File([], fileName);
        Object.defineProperty(loadedFile, 'size', { value: fileSize });

        this.store.addFiles([loadedFile]);
      }
      this.store.updateFileUpload(
        {
          file: null,
          fileName: fileName,
          fileSize: fileSize,
          fileType: '',
          isValid: true
        },
        ''
      );
    }
  }

  async onChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.store.setProcessing(true);
    try {
      await this.fileService.processUploadedFile(input.files);
    } finally {
      this.store.setProcessing(false);
    }
  }

  onDragOver(event: Event) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDropSuccess(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);

    if (this.files().length > 0) return;
    if (event.dataTransfer?.files?.length) {
      this.onChange({ target: { files: event.dataTransfer.files } }  as unknown as Event);
    }
  }

  removeFile(index: number) {
    this.store.removeFile(index);

    if (this.files().length === 0 && this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
      this.store.updateFileUpload(
        {
          file: null,
          fileName: '',
          fileSize: 0,
          fileType: '',
          isValid: false
        },
        'Upload a file'
      );
      this.store.updateManifestInfo({
        isValid: false
      });
    }
  }
}
